import { useCallback, useMemo, useState, type ReactNode } from 'react'

import {
  HealthBoardContext,
  SERVER_RETRY_ACTION,
  ServerLinkContext,
  SERVER_SUBJECT,
  formatMoment,
  selectLiveStatus,
  createHealthBoard,
  serverHealth,
  summarize,
  useHealthBoard,
  useHealthBoardSignals,
  type LiveStatus,
  type ServerLink,
} from '@krp/client-runtime'
import { HealthDot } from '@krp/ui-kit'

import { connectionCheckRequested, selectConnection } from './connection'
import { useAppDispatch, useAppSelector } from './hooks'
import type { RootState } from './types'

const selectLive = (state: RootState): LiveStatus => selectLiveStatus(state.live)

/** Момент «сегодня в 12:57» на момент показа мини-окна. */
const momentText = (iso: string) => formatMoment(iso)

/**
 * Связь окна для экранов: доска сигналов (RP-UI-LINK) и срез связи с сервером (RP-SALE-FAST, шаг 5). Стоит над маршрутизатором
 * и общим окном связи — оба должны её видеть: экран продажи объявляет на доске «связь показываю сам», и общее окно по центру на это время
 * скрыто. Доска создаётся один раз; срез связи экраны читают контекстом (`useLinkStrip`), `RootState` пакеты не знают.
 */
export function ShellHealthRoot({ children }: { children: ReactNode }) {
  const [board] = useState(createHealthBoard)
  const dispatch = useAppDispatch()
  const { status, since, attempts, lastOkAt } = useAppSelector(selectConnection)
  const retryNow = useCallback(() => dispatch(connectionCheckRequested()), [dispatch])
  const link = useMemo<ServerLink>(() => ({ status, since, attempts, lastOkAt, retryNow }), [status, since, attempts, lastOkAt, retryNow])
  return (
    <HealthBoardContext.Provider value={board}>
      <ServerLinkContext.Provider value={link}>{children}</ServerLinkContext.Provider>
    </HealthBoardContext.Provider>
  )
}

/**
 * Точка «Связь» в шапке оболочки (RP-UI-LINK, шаг 2) — одна на всё открытое окно. Сигнал нашего сервера оболочка считает
 * сама (срез `connection` + живой канал), сигналы экранов (источник, касса) приходят с доски окна: экран сообщает свой
 * хуком `useHealthSignal` из `@krp/client-runtime`. Окно «Восстанавливаем соединение» по центру остаётся отдельно
 * (`ConnectionOverlay`): без сервера работать нельзя, поэтому оно блокирует экран — кроме экрана продажи, который показывает связь
 * сам (строка внутри продажи и окна оплаты, чтобы «Отменить» и «Свернуть» не оказались под окном).
 */
export function ShellHealth() {
  const board = useHealthBoard()
  const dispatch = useAppDispatch()
  const connection = useAppSelector(selectConnection)
  const live = useAppSelector(selectLive)
  const screens = useHealthBoardSignals(board)
  const summary = useMemo(() => summarize([serverHealth(connection, live), ...screens]), [connection, live, screens])

  const onAction = (subjectId: string, key: string) => {
    if (subjectId === SERVER_SUBJECT.id) {
      if (key === SERVER_RETRY_ACTION) dispatch(connectionCheckRequested())
      return
    }
    board?.act(subjectId, key)
  }

  // `data-live` — состояние живого канала для проверок стенда (E2E ждёт `online`, прежде чем слать события); человеку его
  // показывает уровень точки.
  return (
    <span className="shell-health" data-live={live}>
      <HealthDot summary={summary} formatMoment={momentText} onAction={onAction} actionId="shell.health" />
    </span>
  )
}
